'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '@/utils/logger';
import Form from '../Form';
import type { FormField, FormFieldValidation, FormValues } from '../Form/Form.types';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useFormPersistence } from '@/hooks/useFormPersistence';
import styles from './ApplicationForm.module.css';
import { csrfTokenManager } from '@/lib/security/csrf/token-manager';
import SuccessState from '../Form/SuccessState';

const initialApplicationFormValues = {
  fullName: '',
  preferredFirstName: '',
  email: '',
  phoneNumber: '',
  genderIdentity: '',
  otherGender: '',
  birthday: '',
  timezone: '',
  otherTimezone: '',
  country: '',
  city: '',
  schoolName: '',
  educationLevel: '',
  otherEducation: '',
  parentGuardianName: '',
  parentGuardianPhone: '',
  parentGuardianEmail: '',
  primaryGoals: [],
  otherGoal: '',
  careerInterests: [],
  learningPreferences: [],
  currentChallenges: [],
  otherChallenge: '',
  timeCommitment: '',
  motivation: [],
  otherMotivation: '',
  excitingActivities: [],
  otherActivity: '',
  helpArea: '',
  otherHelpArea: '',
};

interface ApplicationFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
  buttonText?: string;
  showLabels?: boolean;
  showTitle?: boolean;
  variant?: 'default' | 'minimal' | 'modal';
}

const requiredField = (fieldName: string): FormFieldValidation => ({
  required: `${fieldName} is required`
});

const FORM_STORAGE_KEY = 'timelee_cares_application_form';

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  onSuccess = () => {},
  onCancel = () => {},
  className = '',
  buttonText = 'Submit Application',
  showLabels = true,
  showTitle = true,
  variant = 'default',
}) => {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionData, setSubmissionData] = useState<{}>({});
  const { formData, setFormData, clearFormData } = useFormPersistence(
    FORM_STORAGE_KEY,
    initialApplicationFormValues
  );

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await csrfTokenManager.getToken();
        setCsrfToken(token);
      } catch (error) {
        logger.error('Failed to fetch CSRF token for application form:', error);
      }
    };
    fetchToken();
  }, []);

  const emailValidation: FormFieldValidation = {
    required: 'Email is required',
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address',
    },
  };

  // Improved phone validation with country code and length requirements
  const phoneValidation: FormFieldValidation = {
    required: 'Phone number is required',
    pattern: {
      value: /^\+?[1-9]\d{6,14}$/,
      message: 'Please enter a valid phone number with country code (e.g., +1234567890)',
    }
  };

  // Helper function to check if user is under 18
  const isUnder18 = (birthDate: string): boolean => {
    if (!birthDate) return false;
    
    let date: Date;
    // Check if the date is in YYYY-MM-DD format (HTML5 date input)
    if (/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
      date = new Date(birthDate);
    } 
    // Check if the date is in MM/DD/YYYY format (fallback)
    else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(birthDate)) {
      const [month, day, year] = birthDate.split('/').map(Number);
      date = new Date(year, month - 1, day);
    } 
    // If format is not recognized, assume not under 18 to avoid blocking form submission
    else {
      logger.warn('Unrecognized date format', { birthDate });
      return false;
    }
    
    // If date is invalid, return false to avoid blocking form submission
    if (isNaN(date.getTime())) {
      logger.warn('Invalid date format', { birthDate });
      return false;
    }
    
    const today = new Date();
    const age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    
    // Check if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      return age - 1 < 18;
    }
    return age < 18;
  };

  // Enhanced birthday validation with age check
  const birthdayValidation: FormFieldValidation = {
    required: 'Birthday is required',
    validate: (value, formValues) => {
      // Check if date is in MM/DD/YYYY format
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/;
      if (!dateRegex.test(value)) {
        return 'Please enter a valid date in MM/DD/YYYY format';
      }
      
      // Check if date is valid
      const [month, day, year] = value.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      const now = new Date();
      
      // Check if date components match the input (catches invalid dates like Feb 30)
      if (date.getMonth() + 1 !== month || date.getDate() !== day || date.getFullYear() !== year) {
        return 'Please enter a valid date';
      }
      
      // Check if date is in the future
      if (date > now) {
        return 'Birth date cannot be in the future';
      }
      
      // Check if date is in the past
      if (date >= now) {
        return 'Birthday must be in the past';
      }
      
      // Check if year is reasonable (e.g., not in the future and not too far in the past)
      const minYear = 1900;
      if (year < minYear || year > now.getFullYear()) {
        return `Year must be between ${minYear} and ${now.getFullYear()}`;
      }
      
      return true;
    }
  };

  const fields: FormField[] = [
    // Personal Information Section
    {
      name: 'personal-info-section',
      type: 'section',
      label: 'Personal Information',
    },
    {
      name: 'fullName',
      type: 'text',
      label: showLabels ? 'Full Name' : undefined,
      placeholder: 'Enter your full name',
      showLabel: showLabels,
      validation: requiredField('Full Name'),
      required: true,
    },
    {
      name: 'preferredFirstName',
      type: 'text',
      label: showLabels ? 'Preferred First Name' : undefined,
      placeholder: 'Enter your preferred first name',
      showLabel: showLabels,
      required: false,
    },
    {
      name: 'email',
      type: 'email',
      label: showLabels ? 'Email Address' : undefined,
      placeholder: 'Enter your email address',
      showLabel: showLabels,
      validation: emailValidation,
      required: true,
    },
    {
      name: 'phoneNumber',
      type: 'tel',
      label: showLabels ? 'Phone Number (with country code)' : undefined,
      placeholder: 'e.g., +1234567890',
      showLabel: showLabels,
      validation: phoneValidation,
      required: true,
    },
    {
      name: 'genderIdentity',
      type: 'radio',
      label: showLabels ? 'Gender Identity' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Female', value: 'female' },
        { label: 'Male', value: 'male' },
        { label: 'Non-binary', value: 'non-binary' },
        { label: 'Prefer not to say', value: 'prefer-not-to-say' },
        { label: 'Other', value: 'other' },
      ],
      validation: requiredField('Gender Identity'),
      required: true,
    },
    {
      name: 'otherGender',
      type: 'text',
      label: showLabels ? 'Other Gender Identity' : undefined,
      placeholder: 'Please specify',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => values.genderIdentity === 'other',
    },
    {
      name: 'birthday',
      type: 'date', // Using date type for better UX with native date picker
      label: showLabels ? 'Birthday' : undefined,
      placeholder: 'MM/DD/YYYY',
      showLabel: showLabels,
      validation: {
        ...birthdayValidation,
        // Add client-side validation for the date input
        validate: (value, formValues) => {
          if (!value) return 'Birthday is required';
          
          const date = new Date(value);
          const now = new Date();
          
          // Check if date is in the future
          if (date > now) {
            return 'Birth date cannot be in the future';
          }
          
          
          return true;
        }
      },
      required: true,
    },
    {
      name: 'timezone',
      type: 'select',
      label: showLabels ? 'Current Timezone' : undefined,
      placeholder: 'Select your timezone',
      showLabel: showLabels,
      options: [
        { label: 'Pacific Standard Time (PST)', value: 'PST' },
        { label: 'Mountain Standard Time (MST)', value: 'MST' },
        { label: 'Central Standard Time (CST)', value: 'CST' },
        { label: 'Eastern Standard Time (EST)', value: 'EST' },
        { label: 'Alaska Standard Time (AKST)', value: 'AKST' },
        { label: 'Hawaii-Aleutian Standard Time (HST)', value: 'HST' },
        { label: 'Atlantic Standard Time (AST)', value: 'AST' },
        { label: 'Other', value: 'other' },
      ],
      validation: requiredField('Timezone'),
      required: true,
    },
    {
      name: 'otherTimezone',
      type: 'text',
      label: showLabels ? 'Other Timezone' : undefined,
      placeholder: 'Please specify your timezone',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => values.timezone === 'other',
    },
    {
      name: 'country',
      type: 'text',
      label: showLabels ? 'Country of Residence' : undefined,
      placeholder: 'Enter your country',
      showLabel: showLabels,
      validation: requiredField('Country of Residence'),
      required: true,
    },
    {
      name: 'city',
      type: 'text',
      label: showLabels ? 'City of Residence' : undefined,
      placeholder: 'Enter your city',
      showLabel: showLabels,
      validation: requiredField('City of Residence'),
      required: true,
    },
    {
      name: 'schoolName',
      type: 'text',
      label: showLabels ? 'School Name' : undefined,
      placeholder: 'Enter your current school name',
      showLabel: showLabels,
      required: true,
    },
    {
      name: 'educationLevel',
      type: 'select',
      label: showLabels ? 'Current Educational Level' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Less than high school', value: 'less-than-high-school' },
        { label: '9th grade', value: '9th' },
        { label: '10th grade', value: '10th' },
        { label: '11th grade', value: '11th' },
        { label: '12th grade', value: '12th' },
        { label: 'Associate Degree', value: 'associate-degree' },
        { label: 'Bachelor Degree', value: 'bachelor-degree' },
        { label: 'Other', value: 'other' },
      ],
      validation: requiredField('Current Educational Level'),
      required: true,
    },
    {
      name: 'otherEducation',
      type: 'text',
      label: showLabels ? 'Other Educational Level' : undefined,
      placeholder: 'Please specify',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => values.educationLevel === 'other',
    },
    // Parent/Guardian Information Section
    {
      name: 'parentGuardianSection',
      type: 'section',
      label: 'Parent/Guardian Information',
      showWhen: (values) => {
        // Only show this section if birthday is set and user is under 18
        if (!values.birthday) return false;
        return isUnder18(values.birthday);
      },
    },
    {
      name: 'parentGuardianName',
      type: 'text',
      label: showLabels ? 'Parent/Guardian Full Name' : undefined,
      placeholder: 'Enter parent or guardian full name',
      showLabel: showLabels,
      validation: {
        validate: (value, formValues) => {
          // Only validate if the field is visible (user is under 18)
          if (!formValues.birthday || !isUnder18(formValues.birthday)) {
            return true;
          }
          if (!value || value.trim() === '') {
            return 'Parent/Guardian name is required';
          }
          return true;
        }
      },
      required: false,
      showWhen: (values) => values.birthday && isUnder18(values.birthday),
    },
    {
      name: 'parentGuardianPhone',
      type: 'tel',
      label: showLabels ? 'Parent/Guardian Phone Number' : undefined,
      placeholder: 'Enter parent or guardian phone number',
      showLabel: showLabels,
      validation: {
        validate: (value, formValues) => {
          if (!formValues.birthday || !isUnder18(formValues.birthday)) {
            return true;
          }
          if (!value || value.trim() === '') {
            return 'Parent/Guardian phone number is required';
          }
          if (!/^\+?[1-9]\d{6,14}$/.test(value)) {
            return 'Please enter a valid phone number with country code (e.g., +1234567890)';
          }
          return true;
        }
      },
      required: false,
      showWhen: (values) => values.birthday && isUnder18(values.birthday),
    },
    {
      name: 'parentGuardianEmail',
      type: 'email',
      label: showLabels ? 'Parent/Guardian Email' : undefined,
      placeholder: 'Enter parent or guardian email',
      showLabel: showLabels,
      validation: {
        validate: (value, formValues) => {
          if (!formValues.birthday || !isUnder18(formValues.birthday)) {
            return true;
          }
          if (!value || value.trim() === '') {
            return 'Parent/Guardian email is required';
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            return 'Please enter a valid email address';
          }
          return true;
        }
      },
      required: false,
      showWhen: (values) => values.birthday && isUnder18(values.birthday),
    },
    // Program Interest & Motivation Section
    {
      name: 'program-interest-section',
      type: 'section',
      label: 'Program Interest & Motivation',
    },
    {
      name: 'primaryGoals',
      type: 'checkbox',
      label: showLabels ? 'What is your primary goal for joining this program? (Select all that apply)' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'To explore potential career paths', value: 'explore-career-paths' },
        { label: 'To receive guidance on college applications', value: 'college-guidance' },
        { label: 'To develop skills for personal and professional growth', value: 'skill-development' },
        { label: 'To connect with mentors and like-minded peers', value: 'mentorship-networking' },
        { label: 'Other', value: 'other' },
      ],
      validation: requiredField('Primary Goal'),
      required: true,
    },
    // Explanation fields for each primary goal
    {
      name: 'exploreCareerPathsExplanation',
      type: 'textarea',
      label: showLabels ? 'Please explain your interest in exploring career paths' : undefined,
      placeholder: 'Tell us more about what career areas interest you...',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.primaryGoals) && values.primaryGoals.includes('explore-career-paths'),
    },
    {
      name: 'collegeGuidanceExplanation',
      type: 'textarea',
      label: showLabels ? 'Please explain your need for college guidance' : undefined,
      placeholder: 'Tell us about your college plans and what guidance you need...',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.primaryGoals) && values.primaryGoals.includes('college-guidance'),
    },
    {
      name: 'skillDevelopmentExplanation',
      type: 'textarea',
      label: showLabels ? 'Please explain what skills you want to develop' : undefined,
      placeholder: 'Tell us about the skills you want to work on...',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.primaryGoals) && values.primaryGoals.includes('skill-development'),
    },
    {
      name: 'mentorshipNetworkingExplanation',
      type: 'textarea',
      label: showLabels ? 'Please explain your interest in mentorship and networking' : undefined,
      placeholder: 'Tell us what you hope to gain from mentors and networking...',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.primaryGoals) && values.primaryGoals.includes('mentorship-networking'),
    },
    {
      name: 'otherGoal',
      type: 'textarea',
      label: showLabels ? 'Other Primary Goal' : undefined,
      placeholder: 'Please explain your other primary goal',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.primaryGoals) && values.primaryGoals.includes('other'),
    },
    {
      name: 'careerInterests',
      type: 'checkbox',
      label: showLabels ? 'Which of the following best describes your current career interests? (Select all that apply)' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'STEM (Science, Technology, Engineering, Math)', value: 'stem' },
        { label: 'Arts and Humanities', value: 'arts-humanities' },
        { label: 'Business and Entrepreneurship', value: 'business-entrepreneurship' },
        { label: 'Social Work and Community Service', value: 'social-work' },
        { label: 'I\'m still exploring my options', value: 'exploring' },
      ],
      required: false,
    },
    {
      name: 'learningPreferences',
      type: 'checkbox',
      label: showLabels ? 'How do you prefer to learn and grow? (Select all that apply)' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Hands-on experiences like internships or volunteering', value: 'hands-on' },
        { label: 'One-on-one mentorship and guidance', value: 'mentorship' },
        { label: 'Group workshops and collaborative activities', value: 'group-workshops' },
        { label: 'Online resources and self-paced learning', value: 'online-learning' },
      ],
      required: false,
    },
    {
      name: 'currentChallenges',
      type: 'checkbox',
      label: showLabels ? 'What challenges do you currently face in preparing for college or a career? (Select all that apply)' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Lack of information about opportunities', value: 'lack-info' },
        { label: 'Difficulty identifying my strengths and interests', value: 'identify-strengths' },
        { label: 'Financial barriers to pursuing higher education', value: 'financial-barriers' },
        { label: 'Limited access to mentors or role models', value: 'limited-mentors' },
        { label: 'Other', value: 'other' },
      ],
      required: false,
    },
    {
      name: 'otherChallenge',
      type: 'textarea',
      label: showLabels ? 'Other Challenge' : undefined,
      placeholder: 'Please describe other challenges you face',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.currentChallenges) && values.currentChallenges.includes('other'),
    },
    {
      name: 'timeCommitment',
      type: 'radio',
      label: showLabels ? 'How much time are you willing to dedicate to this program each week?' : undefined,
      showLabel: showLabels,
      options: [
        { label: '1-2 hours', value: '1-2-hours' },
        { label: '3-5 hours', value: '3-5-hours' },
        { label: '6-8 hours', value: '6-8-hours' },
        { label: 'More than 8 hours', value: 'more-than-8-hours' },
      ],
      validation: requiredField('Time Commitment'),
      required: true,
    },
    {
      name: 'motivation',
      type: 'checkbox',
      label: showLabels ? 'What motivates you to succeed in your academic or career journey? (Select all that apply)' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Achieving personal goals and dreams', value: 'personal-goals' },
        { label: 'Making a positive impact on my community', value: 'community-impact' },
        { label: 'Gaining financial independence', value: 'financial-independence' },
        { label: 'Making my family proud', value: 'family-proud' },
        { label: 'Other', value: 'other' },
      ],
      validation: requiredField('Motivation'),
      required: true,
    },
    {
      name: 'otherMotivation',
      type: 'textarea',
      label: showLabels ? 'Other Motivation' : undefined,
      placeholder: 'Please describe what motivates you',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.motivation) && values.motivation.includes('other'),
    },
    {
      name: 'excitingActivities',
      type: 'checkbox',
      label: showLabels ? 'Which of the following activities excites you the most? (Select all that apply)' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Participating in career exploration workshops', value: 'career-workshops' },
        { label: 'Learning how to write a strong resume or college essay', value: 'resume-writing' },
        { label: 'Networking with professionals in different fields', value: 'networking' },
        { label: 'Volunteering for community service projects', value: 'volunteering' },
        { label: 'Other', value: 'other' },
      ],
      required: false,
    },
    {
      name: 'otherActivity',
      type: 'textarea',
      label: showLabels ? 'Other Exciting Activity' : undefined,
      placeholder: 'Please describe other activities that excite you',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => Array.isArray(values.excitingActivities) && values.excitingActivities.includes('other'),
    },
    {
      name: 'helpArea',
      type: 'radio',
      label: showLabels ? 'If you could receive help in one area, what would it be?' : undefined,
      showLabel: showLabels,
      options: [
        { label: 'Choosing the right college or major', value: 'college-major' },
        { label: 'Finding internships or volunteer opportunities', value: 'internships-volunteering' },
        { label: 'Building confidence and communication skills', value: 'confidence-communication' },
        { label: 'Understanding financial aid and scholarships', value: 'financial-aid' },
        { label: 'Other', value: 'other' },
      ],
      validation: requiredField('Help Area'),
      required: true,
    },
    {
      name: 'otherHelpArea',
      type: 'textarea',
      label: showLabels ? 'Other Help Area' : undefined,
      placeholder: 'Please describe the area where you need help',
      showLabel: showLabels,
      required: false,
      hidden: true,
      showWhen: (values) => values.helpArea === 'other',
    },
  ];

  const handleSubmit = useCallback(async (values: FormValues, options?: { headers?: Record<string, string> }) => {
    try {
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');

      const token = options?.headers?.['x-csrf-token'] || values._csrf || csrfToken;
      if (token) {
        headers.append('CSRF-Token', token);
      }

      const response = await fetch('/api/apply', {
        method: 'POST',
        headers: Object.fromEntries(headers.entries()),
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit application');
      }
      
      const data = await response.json();
      
      // Mark as submitted to show success state
      setIsSubmitted(true);
      
      // Clear the form data from local storage
      clearFormData();
      
      // Call the onSuccess callback if provided (for non-modal variants)
      if (onSuccess && variant !== 'modal') {
        onSuccess();
      }
      
      return { success: true };
    } catch (error) {
      logger.error('Error submitting application form:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to submit application. Please try again.' 
      };
    }
  }, [onSuccess, clearFormData, csrfToken]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    return Object.keys(initialApplicationFormValues).some(
      (key) => {
        const formKey = key as keyof typeof initialApplicationFormValues;
        return formData[formKey] !== initialApplicationFormValues[formKey];
      }
    );
  }, [formData]);

  // Handle form changes
  const handleFormChange = (values: FormValues) => {
    setFormData(prev => ({
      ...prev,
      ...values
    }));
  };

  // Handle closing the success dialog
  const handleCloseSuccess = useCallback(() => {
    if (variant === 'modal') {
      // In modal variant, close the success state and call onSuccess
      setIsSubmitted(false);
      setSubmissionData({});
      clearFormData();
      if (onSuccess) onSuccess();
    } else {
      // In regular variant, call the parent's onSuccess callback
      setIsSubmitted(false);
      setSubmissionData({});
      clearFormData();
      if (onSuccess) onSuccess();
    }
  }, [clearFormData, onSuccess, variant]);

  // Reset the form to allow new submissions
  const handleResetForm = () => {
    setIsSubmitted(false);
    setSubmissionData({});
    clearFormData();
  };

  // Handle confirm cancel
  const handleConfirmCancel = useCallback(() => {
    clearFormData();
    if (onCancel) {
      onCancel();
    }
  }, [clearFormData, onCancel]);

  // Handle cancel/close
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowConfirmDialog(true);
    } else {
      handleConfirmCancel();
    }
  }, [hasUnsavedChanges, handleConfirmCancel]);

  // Show success state after form submission
  if (isSubmitted) {
    return (
      <div className={`${styles.successContainer} ${className}`}>
        <SuccessState
          onClose={handleCloseSuccess}
        />
      </div>
    );
  }

  // Form description
  const formDescription = 'Complete this form to apply for TimeLee CARES Foundation support. After submission, you\'ll receive an email confirmation with next steps.';

  return (
    <>
      <Form
        title={variant === 'modal' ? '' : (showTitle ? 'Application Form' : '')}
        description={formDescription}
        fields={fields}
        onSubmit={handleSubmit}
        onChange={handleFormChange}
        initialValues={formData}
        submitButtonText={buttonText}
        className={className}
        formClassName={styles.form}
        fieldClassName={`${styles.field} ${variant === 'minimal' ? styles.minimal : ''}`}
        inputClassName={styles.input}
        textareaClassName={styles.textarea}
        buttonWrapperClassName={styles.buttonWrapper}
        submitButtonClassName={styles.submitButton}
        resetButtonClassName={styles.resetButton}
        errorClassName={styles.error}
        successClassName={styles.success}
        loadingClassName={styles.loading}
        enableCSRF={true}
        enableTurnstile={true}
        turnstileSiteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
        turnstileResetOnSubmit={true}
        onCancel={handleCancel}
        onError={(error) => {
          logger.error('Form submission error', { error });
        }}
        onReset={() => {
          clearFormData();
          // Force the Form component to use the fresh initial values
          setFormData(initialApplicationFormValues);
        }}
        renderBeforeFields={csrfToken ? (() => (
          <input key="csrf-token" type="hidden" name="_csrf" value={csrfToken} />
        )) : undefined}
      />

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave?"
        confirmText="Discard Changes"
        cancelText="Continue Editing"
        onConfirm={() => {
          clearFormData();
          setShowConfirmDialog(false);
          if (onCancel) onCancel();
        }}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </>
  );
};

export default ApplicationForm;